import React, { useState } from "react";
import { ChevronRight, Download, FileText, Trash2, UserCheck } from "lucide-react";
import { downloadFile } from "../lib/api";
import { ClientFilter, clientOptions } from "../components/filters/ClientFilter";
import { StatusBadge as RecordStatusBadge } from "../components/StatusBadge";
import { SortHeadCell } from "../components/sorting/SortableTh";
import { sortRows, useSortPreference } from "../lib/sorting";
import { usePager } from "../lib/usePager";
import { Pager } from "../components/table/Pager";

const moduleKind = key => /status$/.test(key) ? "state"
  : /(date|_at)$/.test(key) ? "date"
  : ["taxable", "total", "residual", "days_remaining", "nearest_days", "days_to_renewal"].includes(key) ? "number" : "text";


function Module({ page, rows, team, user, onAssign, onDelete, collectionFor, clients = [] }) {
  const [clientFilter, setClientFilter] = useState("");
  const cols = page === "clients" ? ["name", "contact", "email", "status"]
    : page === "deadlines" ? ["title", "site", "type", "date", "days_remaining", "status"]
    : page === "practices" ? ["name", "site", "type", "nearest_days", "expiry_status", "scia_status"]
    : page === "wells" ? ["name", "site", "renewal_due_date", "days_to_renewal", "renewal_status", "reading_status"]
    : page === "finance" ? ["client_name", "name", "taxable", "total", "residual", "payment_status"]
    : ["name", "type", "site", "status"];
  const teamMap = Object.fromEntries(team.map((t) => [t.id, t.name]));
  const collection = collectionFor(page);
  const { sort, onSort } = useSortPreference(`module_${page}`, null);
  const scoped = clientFilter ? rows.filter(r => (r.client_name || r.client || r.recipients || "").trim() === clientFilter) : rows;
  const ordered = sortRows(scoped, sort);
  const pager = usePager(ordered, 50);
  return (
    <section className="module-section">
      <div className="table-toolbar">
        <span>{ordered.length} elementi{ordered.length !== rows.length ? ` su ${rows.length}` : ""} · formule aggiornate oggi</span>
        {page === "finance" && <ClientFilter value={clientFilter} onChange={setClientFilter} options={clientOptions(rows.map(r => r.client_name || r.client || r.recipients))} testId="finance-filter-client" />}
        <button className="filter-button" data-testid="filter-button">Filtri <ChevronRight size={15} /></button>
      </div>
      <div className="data-table">
        <div className="table-head" style={{gridTemplateColumns:`repeat(${cols.length}, 1fr) 140px 80px`}}>
          {cols.map((c) => <SortHeadCell key={c} label={c === "client_name" ? "cliente" : c.replaceAll("_", " ")} sortKey={c} kind={moduleKind(c)}
            sort={sort} onSort={onSort} testId={`module-sort-${c}`} />)}
          <span>Assegnato a</span>
          <span>Azioni</span>
        </div>
        {ordered.length ? pager.visible.map((r, i) => {
          const rowCollection = page === "finance" ? (r.total || r.taxable ? "economic" : "quotes") : page === "deadlines" ? (r.event_date ? "activities" : "deadlines") : collection;
          const assignee = r.assigned_to ? (teamMap[r.assigned_to] || (r.assigned_to === user.id ? "Tu" : "—")) : null;
          const canSelfClaim = !r.assigned_to;
          const canManage = user.role === "admin" || r.assigned_to === user.id;
          return (
            <div className="table-row" key={r.id || i} data-testid={`table-row-${i}`} style={{gridTemplateColumns:`repeat(${cols.length}, 1fr) 140px 80px`}}>
              {cols.map((c) => <span key={c} data-testid={c === "client_name" ? `row-client-${i}` : undefined}>{["taxable", "total", "residual"].includes(c) ? `€ ${r[c] ?? "0,00"}` : /status$/.test(c) ? <RecordStatusBadge status={r[c]} testId={`row-status-${c}-${i}`} /> : c === "client_name" ? (r.client_name || r.client || r.recipients || "Cliente da associare") : r[c] ?? "—"}</span>)}
              <span data-testid={`row-assignee-${i}`}>
                {user.role === "admin" ? (
                  <select data-testid={`assign-select-${i}`} value={r.assigned_to || ""} onChange={(e) => onAssign(rowCollection, r.id, e.target.value || null)} className="row-select">
                    <option value="">Non assegnato</option>
                    <option value={user.id}>Me stesso</option>
                    {team.filter((t) => t.id !== user.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                ) : assignee ? <span className="row-assignee"><UserCheck size={12} /> {assignee}</span>
                  : <button data-testid={`claim-button-${i}`} onClick={() => onAssign(rowCollection, r.id, user.id)} className="claim-btn">Prendi in carico</button>
                }
              </span>
              <span>
                {page === "finance" && r.id && <button data-testid={`pdf-button-${i}`} onClick={() => downloadFile(`/${rowCollection}/${r.id}/pdf`, `${r.name || "documento"}.pdf`)} className="icon-btn" title="Scarica PDF"><Download size={14} /></button>}
                {canManage && <button data-testid={`delete-button-${i}`} onClick={() => onDelete(rowCollection, r.id)} className="icon-btn" title="Elimina"><Trash2 size={14} /></button>}
              </span>
            </div>
          );
        }) : <div className="empty"><FileText size={28} /><strong>Nessun elemento registrato</strong><span>Usa "Nuovo elemento" per aggiungere il primo.</span></div>}
      </div>
      <Pager pager={pager} label="elementi" testId={`module-pager-${page}`} />
    </section>
  );
}


export { Module };
